import { Entries, ValueOf, WithId } from '../../../types/common'

/**
 * Returns the typed version of Object.keys
 * @param obj
 * @returns
 */
export const keys = <T extends Object>(obj: T): (keyof T)[] =>
  Object.keys(obj) as (keyof T)[]

/**
 * Creates an object with the same keys as object and values generated by running each own enumerable
 * string keyed property of object through iteratee. The iteratee is invoked with three arguments: (value, key, object).
 * @param {Function} mapFn
 * @param {Object} obj
 */
export const mapValues = <Source extends Object, TargetVal>(
  mapFn: (value: ValueOf<Source>, key: keyof Source, obj: Source) => TargetVal,
  obj: Source
) =>
  keys(obj).reduce(
    (acc, key) => ({ ...acc, [key]: mapFn(obj[key], key, obj) }),
    {}
  ) as Record<keyof Source, TargetVal>

/**
 * util to filter objects
 * @param keepFn filter function that accept as first param value and second key of object
 * @param obj object we want filter
 * @returns object with keys filtered
 */
export const filterObject = <Source extends {}>(
  keepFn: (val: ValueOf<Source>, key?: keyof Source, obj?: Source) => boolean,
  obj: Source
) =>
  keys(obj).reduce(
    (acc, key) => ({
      ...acc,
      ...(keepFn(obj[key], key, obj) ? { [key]: obj[key] } : {}),
    }),
    {}
  ) as Partial<Source>

/**
 * Returns the typed version of Object.entries
 * @param obj
 */
export const entries = <T extends Object>(obj: T): Entries<T> =>
  Object.entries(obj) as any

/**
 * Returns the id of the given object
 * @param item
 */
export const getId = <TObject extends WithId>(item: TObject) => item.id

/**
 * NON-efficient way to compare 2 objects.
 * Use with caution & at your own risk.
 *
 * @param obj1
 * @param obj2
 * @returns true if obj are equals
 */
export const equals = (obj1: any, obj2: any) =>
  JSON.stringify(obj1) === JSON.stringify(obj2)
